// UPPDRAG-8 PR-B+C+D — dual-write helpers for the 1-week shadow window.
//
// These wrap the existing legacy-table writes (direct_messages,
// group_chat_messages, training_messages, message_read_state) so that
// every send / mark-read / pin also lands in the new polymorphic
// chat_conversations + chat_conversation_participants + chat_messages
// schema.
//
// Strategy:
//   1. Write to LEGACY first (it remains the read source-of-truth during
//      shadow-read window). If legacy write fails, throw — user-visible
//      error preserves today's behaviour.
//   2. Then write to NEW. If new write fails, log a warning but DO NOT
//      throw — legacy already succeeded, the user's send happened, and
//      the new-side mismatch will surface in the shadow-read parity check.
//      PR-F removes legacy + this dual-write logic.
//
// The helpers are pure-ish: they take an injected supabase client. Tests
// in src/test/business/chatDualWrite.test.ts use mocked clients.

import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";

type Client = SupabaseClient<Database>;

/**
 * Internal helper — log new-write failures during shadow window. Never throws.
 */
function logShadowFailure(context: string, error: unknown) {
  // eslint-disable-next-line no-console
  console.warn(
    `[chatDualWrite] new-schema write failed (${context}); legacy succeeded. ` +
      `Shadow-write divergence — investigate.`,
    error,
  );
}

// ─── Direct messages (DM) ──────────────────────────────────────────────

export interface SendDirectMessageArgs {
  senderUserId: string;
  recipientUserId: string;
  organizationId: string;
  body: string;
}

/**
 * Send a direct message. Writes to legacy `direct_messages` (source of
 * truth) AND to the new `chat_messages` via the
 * `get_or_create_direct_conversation` RPC.
 *
 * Returns the legacy `direct_messages.id` for backwards compatibility.
 * Callers that want the new chat_message id can pass `returnNewId: true`
 * but most don't need it during shadow phase.
 */
export async function sendDirectMessage(
  client: Client,
  args: SendDirectMessageArgs,
): Promise<{ legacyId: string }> {
  // 1. LEGACY write (source of truth during shadow window)
  const { data: legacy, error: legacyErr } = await client
    .from("direct_messages")
    .insert({
      sender_id: args.senderUserId,
      recipient_id: args.recipientUserId,
      message: args.body,
    })
    .select("id")
    .single();
  if (legacyErr || !legacy) {
    throw legacyErr ?? new Error("direct_messages insert returned no row");
  }
  const legacyId = (legacy as { id: string }).id;

  // 2. NEW write (best-effort during shadow window)
  try {
    const { data: convId, error: rpcErr } = await client.rpc(
      "get_or_create_direct_conversation",
      {
        _other_user_id: args.recipientUserId,
        _organization_id: args.organizationId,
      },
    );
    if (rpcErr || !convId) {
      logShadowFailure("get_or_create_direct_conversation", rpcErr);
    } else {
      const { error: msgErr } = await client.from("chat_messages").insert({
        conversation_id: convId as unknown as string,
        sender_id: args.senderUserId,
        body: args.body,
        data: { legacy_dm_id: legacyId, shadow_write: true },
      });
      if (msgErr) logShadowFailure("chat_messages insert (DM)", msgErr);
    }
  } catch (err) {
    logShadowFailure("DM dual-write", err);
  }

  return { legacyId };
}

/**
 * Mark unread DMs as read. Writes legacy `direct_messages.read_at` AND
 * new `chat_conversation_participants.last_read_at` for the (self,
 * sender) conversation.
 */
export async function markDirectMessagesRead(
  client: Client,
  args: {
    selfUserId: string;
    partnerUserId: string;
    organizationId: string;
    unreadMessageIds: string[];
  },
): Promise<void> {
  if (args.unreadMessageIds.length === 0) return;
  const now = new Date().toISOString();

  // 1. LEGACY: per-message read_at
  const { error: legacyErr } = await client
    .from("direct_messages")
    .update({ read_at: now })
    .in("id", args.unreadMessageIds);
  if (legacyErr) {
    throw legacyErr;
  }

  // 2. NEW: per-conversation watermark on chat_conversation_participants
  try {
    const { data: convId, error: rpcErr } = await client.rpc(
      "get_or_create_direct_conversation",
      {
        _other_user_id: args.partnerUserId,
        _organization_id: args.organizationId,
      },
    );
    if (rpcErr || !convId) {
      logShadowFailure("get_or_create_direct_conversation (mark read)", rpcErr);
      return;
    }
    const { error: updErr } = await client
      .from("chat_conversation_participants")
      .update({ last_read_at: now })
      .eq("conversation_id", convId as unknown as string)
      .eq("user_id", args.selfUserId);
    if (updErr) logShadowFailure("participants.last_read_at (DM)", updErr);
  } catch (err) {
    logShadowFailure("DM mark-read dual-write", err);
  }
}

// ─── Group chat (training_group) ───────────────────────────────────────

export interface SendGroupMessageArgs {
  groupId: string;
  senderUserId: string;
  body: string;
  isSystem?: boolean;
}

/**
 * Send a group message. Writes legacy `group_chat_messages` AND new
 * `chat_messages` via `get_or_create_training_group_conversation` RPC.
 */
export async function sendGroupMessage(
  client: Client,
  args: SendGroupMessageArgs,
): Promise<{ legacyId: string }> {
  // 1. LEGACY
  const { data: legacy, error: legacyErr } = await client
    .from("group_chat_messages")
    .insert({
      group_id: args.groupId,
      sender_id: args.senderUserId,
      message: args.body,
      is_system: args.isSystem ?? false,
    })
    .select("id")
    .single();
  if (legacyErr || !legacy) {
    throw legacyErr ?? new Error("group_chat_messages insert returned no row");
  }
  const legacyId = (legacy as { id: string }).id;

  // 2. NEW
  try {
    const { data: convId, error: rpcErr } = await client.rpc(
      "get_or_create_training_group_conversation",
      { _group_id: args.groupId },
    );
    if (rpcErr || !convId) {
      logShadowFailure("get_or_create_training_group_conversation", rpcErr);
    } else {
      const { error: msgErr } = await client.from("chat_messages").insert({
        conversation_id: convId as unknown as string,
        sender_id: args.senderUserId,
        body: args.body,
        is_system: args.isSystem ?? false,
        data: { legacy_group_msg_id: legacyId, shadow_write: true },
      });
      if (msgErr) logShadowFailure("chat_messages insert (group)", msgErr);
    }
  } catch (err) {
    logShadowFailure("group dual-write", err);
  }

  return { legacyId };
}

/**
 * Pin or unpin a message in the group chat. Updates BOTH legacy
 * `group_chat_messages.pinned` AND the corresponding new `chat_messages`
 * row (looked up by metadata.legacy_group_msg_id).
 *
 * Returns silently if the new-side row can't be located — pinning is
 * non-critical UX; legacy still has the canonical pin state.
 */
export async function setGroupMessagePinned(
  client: Client,
  args: {
    legacyMessageId: string;
    pinned: boolean;
    pinnedByUserId: string;
  },
): Promise<void> {
  // 1. LEGACY
  const { error: legacyErr } = await client
    .from("group_chat_messages")
    .update({
      pinned: args.pinned,
      pinned_by: args.pinned ? args.pinnedByUserId : null,
    })
    .eq("id", args.legacyMessageId);
  if (legacyErr) throw legacyErr;

  // 2. NEW (best-effort lookup by legacy_group_msg_id)
  try {
    const { data: rows, error: lookupErr } = await client
      .from("chat_messages")
      .select("id")
      .filter("data->>legacy_group_msg_id", "eq", args.legacyMessageId)
      .limit(1);
    if (lookupErr) {
      logShadowFailure("chat_messages lookup by legacy_group_msg_id", lookupErr);
      return;
    }
    const targetId = rows?.[0]?.id;
    if (!targetId) {
      logShadowFailure(
        "set group pinned: no new-side row found for legacy id " + args.legacyMessageId,
        null,
      );
      return;
    }
    const { error: updErr } = await client
      .from("chat_messages")
      .update({
        pinned: args.pinned,
        pinned_at: args.pinned ? new Date().toISOString() : null,
        pinned_by: args.pinned ? args.pinnedByUserId : null,
      })
      .eq("id", targetId);
    if (updErr) logShadowFailure("chat_messages pinned UPDATE", updErr);
  } catch (err) {
    logShadowFailure("group pin dual-write", err);
  }
}

// ─── Training parent-coach 1:1 ─────────────────────────────────────────

export interface SendTrainingMessageArgs {
  groupId: string;
  organizationId: string;
  senderUserId: string;
  recipientUserId: string;
  childUserId: string;
  coachUserId: string;
  parentUserId: string;
  body: string;
}

/**
 * Send a parent-coach 1:1 message about a child. Writes legacy
 * `training_messages` AND new `chat_messages` via
 * `get_or_create_training_parent_coach_conversation` RPC.
 */
export async function sendTrainingMessage(
  client: Client,
  args: SendTrainingMessageArgs,
): Promise<{ legacyId: string }> {
  // 1. LEGACY
  const { data: legacy, error: legacyErr } = await client
    .from("training_messages")
    .insert({
      organization_id: args.organizationId,
      group_id: args.groupId,
      sender_id: args.senderUserId,
      recipient_id: args.recipientUserId,
      player_id: args.childUserId,
      message: args.body,
    })
    .select("id")
    .single();
  if (legacyErr || !legacy) {
    throw legacyErr ?? new Error("training_messages insert returned no row");
  }
  const legacyId = (legacy as { id: string }).id;

  // 2. NEW
  try {
    const { data: convId, error: rpcErr } = await client.rpc(
      "get_or_create_training_parent_coach_conversation",
      {
        _group_id: args.groupId,
        _parent_user_id: args.parentUserId,
        _coach_user_id: args.coachUserId,
        _child_user_id: args.childUserId,
      },
    );
    if (rpcErr || !convId) {
      logShadowFailure("get_or_create_training_parent_coach_conversation", rpcErr);
    } else {
      const { error: msgErr } = await client.from("chat_messages").insert({
        conversation_id: convId as unknown as string,
        sender_id: args.senderUserId,
        body: args.body,
        data: { legacy_training_msg_id: legacyId, shadow_write: true },
      });
      if (msgErr) logShadowFailure("chat_messages insert (training)", msgErr);
    }
  } catch (err) {
    logShadowFailure("training dual-write", err);
  }

  return { legacyId };
}

// ─── League match (match_chat_messages with match_id, NEW PR-E) ───────

export interface SendLeagueMatchMessageArgs {
  matchId: string;            // league_matches.id
  senderUserId: string | null;  // NULL for system messages with no actor
  body: string;
  isSystem?: boolean;
  /**
   * Optional structured metadata for the new chat_messages.data jsonb.
   * Used by reschedule cards, admin callouts, result reports, etc., so
   * future renderers can replace the emoji-prefixed text with structured
   * components. Falls back to body-only if absent.
   *
   * Examples:
   *   { type: 'reschedule_proposal', proposed_date, proposed_time }
   *   { type: 'admin_callout' }
   *   { type: 'result_reported', sets_summary, reporter_name }
   *   { type: 'reschedule_admin_decision', decision: 'approved' | 'denied' }
   *   { type: 'booking_cascade', kind: 'moved' | 'cancelled', reason }
   */
  data?: Record<string, unknown> | null;
}

/**
 * Send a league-match chat message. Writes legacy `match_chat_messages`
 * (match_id) AND new `chat_messages` via the
 * `get_or_create_league_match_conversation` RPC.
 */
export async function sendLeagueMatchMessage(
  client: Client,
  args: SendLeagueMatchMessageArgs,
): Promise<{ legacyId: string }> {
  // 1. LEGACY (source of truth)
  const { data: legacy, error: legacyErr } = await client
    .from("match_chat_messages")
    .insert({
      match_id: args.matchId,
      user_id: args.senderUserId,
      message: args.body,
      is_system: args.isSystem ?? false,
    } as never)
    .select("id")
    .single();
  if (legacyErr || !legacy) {
    throw legacyErr ?? new Error("match_chat_messages insert returned no row");
  }
  const legacyId = (legacy as { id: string }).id;

  // 2. NEW (best-effort)
  try {
    const { data: convId, error: rpcErr } = await client.rpc(
      "get_or_create_league_match_conversation",
      { _match_id: args.matchId },
    );
    if (rpcErr || !convId) {
      logShadowFailure("get_or_create_league_match_conversation", rpcErr);
    } else {
      const { error: msgErr } = await client.from("chat_messages").insert({
        conversation_id: convId as unknown as string,
        sender_id: args.senderUserId,
        body: args.body,
        is_system: args.isSystem ?? false,
        data: {
          legacy_match_chat_id: legacyId,
          shadow_write: true,
          ...(args.data ?? {}),
        },
      });
      if (msgErr) logShadowFailure("chat_messages insert (league match)", msgErr);
    }
  } catch (err) {
    logShadowFailure("league match dual-write", err);
  }

  return { legacyId };
}

// ─── Open match (match_chat_messages with open_match_v2_id) ────────────
// Spår 1b: V2-chat rivet med open_matches_v2-tabellen. sendOpenMatchMessage
// export borttagen — endast tester importerade den, och tester följer med
// V2-rivningen. Andra dual-write-funktioner (direct/group/training/league)
// behålls oförändrade.

// ─── Conversation read watermark (shared) ──────────────────────────────

/**
 * Generic: when the user marks a conversation as read in the legacy
 * `message_read_state` table, also bump
 * `chat_conversation_participants.last_read_at` for the matching
 * conversation. The `legacyType → newType` mapping is:
 *   'group_chat'        → 'training_group'
 *   'open_match_chat'   → 'open_match'    (PR-E will write the new side; this PR ignores)
 *   'match_chat'        → 'league_match'  (PR-E will write the new side; this PR ignores)
 *
 * For 'direct' / 'training': those use per-message read_at on legacy
 * tables; their dual-write is in `markDirectMessagesRead` /
 * `markTrainingMessagesRead`.
 */
export async function dualWriteMessageReadState(
  client: Client,
  args: {
    userId: string;
    legacyConversationType: "group_chat" | "open_match_chat" | "match_chat";
    sourceId: string; // group_id / activity_id / league_match_id
    selfUserId: string;
  },
): Promise<void> {
  const now = new Date().toISOString();

  // 1. LEGACY upsert
  const { error: legacyErr } = await client
    .from("message_read_state" as never)
    .upsert(
      {
        user_id: args.userId,
        conversation_type: args.legacyConversationType,
        source_id: args.sourceId,
        last_read_at: now,
      } as never,
      { onConflict: "user_id,conversation_type,source_id" },
    );
  if (legacyErr) throw legacyErr;

  // 2. NEW (only for group_chat in this PR; PR-E adds the others)
  if (args.legacyConversationType !== "group_chat") return;

  try {
    const { data: convId, error: rpcErr } = await client.rpc(
      "get_or_create_training_group_conversation",
      { _group_id: args.sourceId },
    );
    if (rpcErr || !convId) {
      logShadowFailure("get_or_create_training_group_conversation (mark read)", rpcErr);
      return;
    }
    const { error: updErr } = await client
      .from("chat_conversation_participants")
      .update({ last_read_at: now })
      .eq("conversation_id", convId as unknown as string)
      .eq("user_id", args.selfUserId);
    if (updErr) logShadowFailure("participants.last_read_at (group)", updErr);
  } catch (err) {
    logShadowFailure("group mark-read dual-write", err);
  }
}
